/**
 * Réduit une répétition résiduelle à une seule occurrence (C2, filet
 * d'AFFICHAGE — post-mortem Dana 2026-08, « Buydynamicly continuing » ×641).
 *
 * WHY une passe front en plus du garde-fou backend : le garde-fou runtime
 * (`core/MasterLoop.ts`, les deux agents) coupe le FLUX en direct dès qu'une
 * dégénérescence est détectée, mais deux surfaces peuvent encore afficher une
 * répétition figée sans jamais retraverser ce chemin — un message HISTORIQUE
 * rejoué depuis avant l'ajout du garde-fou runtime, et tout agent (CD-v3) qui
 * n'implémente pas la coupure en flux. Cette passe est donc un filet
 * d'affichage, jamais la garde primaire : elle ne raccourcit pas le temps de
 * génération, elle nettoie ce qui a déjà été généré.
 *
 * Mêmes seuils, volontairement larges, que le détecteur runtime : une fenêtre
 * de 40 caractères répétée plus de 12 fois d'affilée, ou un même token de 6 à
 * 30 caractères (sans espace) répété plus de 30 fois d'affilée. Ne JAMAIS
 * confondre une énumération légitime (liste de pièces répétitive, où chaque
 * occurrence diffère par au moins un caractère) avec une dégénérescence — les
 * deux motifs exigent une identité STRICTE de la sous-chaîne répétée.
 */

/** Fenêtre de caractères testée pour une répétition. */
const WINDOW = 40;
/** Nombre de répétitions d'affilée strictement nécessaire pour agir (>12). */
const WINDOW_REPEATS = 12;
/** Longueur d'un token candidat (sans espace). */
const TOKEN_MIN = 6;
const TOKEN_MAX = 30;
/** Nombre de répétitions d'affilée strictement nécessaire pour agir (>30). */
const TOKEN_REPEATS = 30;

// `.` exclut le saut de ligne par défaut : une répétition ne peut donc jamais
// être détectée à travers une frontière de paragraphe, ce qui protège une
// énumération multi-lignes légitime.
const WINDOW_REPEAT_RE = new RegExp(`(.{${WINDOW}})\\1{${WINDOW_REPEATS},}`, 'g');
// Quantificateur PARESSEUX (`?` après `{min,max}`) sur le groupe capturé : un
// quantificateur glouton essaierait d'abord 30 caractères, et pour une unité
// dégénérée dont la période réelle est un diviseur de 30 (ex. 10, 15) le moteur
// s'arrêterait sur un multiple de la période au lieu de la période minimale —
// le texte collapserait vers 2-3 occurrences résiduelles au lieu d'une seule.
// Le paresseux teste la période la plus COURTE d'abord, donc la première qui
// « boucle » assez de fois est la vraie période minimale.
const TOKEN_REPEAT_RE = new RegExp(`(\\S{${TOKEN_MIN},${TOKEN_MAX}}?)\\1{${TOKEN_REPEATS},}`, 'g');

/**
 * Texte → texte, idempotent : `repetitionCollapse(repetitionCollapse(x)) === repetitionCollapse(x)`
 * (une répétition déjà réduite à une occurrence ne peut plus, par construction,
 * atteindre le seuil de déclenchement).
 */
export function repetitionCollapse(text: string): string {
  if (!text) return text;
  let out = text.replace(WINDOW_REPEAT_RE, (_match, unit: string) => unit);
  out = out.replace(TOKEN_REPEAT_RE, (_match, unit: string) => unit);
  return out;
}
